import { type SyntheticEvent, useCallback, useState } from 'react'

import { AlertBox } from '@/components/common/AlertBox'
import { ButtonCheckbox, ButtonDefault, ButtonText } from '@/components/common/Button'
import { InputTextarea, InputTypeahead } from '@/components/common/Input'
import { type RecsEndpointInputsType } from '@/utils/apiUtil'

import styles from './RecsForm.module.scss'

type Props = {
  error?: string
  inputs?: RecsEndpointInputsType
  loading?: boolean
  onSubmit: (inputs: RecsEndpointInputsType) => void
}

export const RecsForm = ({ error, inputs, loading, onSubmit }: Props) => {
  const [errorInternal, setErrorInternal] = useState('')
  const [commander, setCommander] = useState(inputs?.commander || '')
  const [commander2, setCommander2] = useState(inputs?.commander2 || '')
  const [decklist, setDecklist] = useState(inputs?.decklist || '')
  const [excludeLands, setExcludeLands] = useState(inputs?.excludeLands || false)

  const disabled = loading || !commander
  const clearable = !!commander || !!commander2 || !!decklist || excludeLands

  const errorDisplay = [error, errorInternal].filter(Boolean).join(', ')

  const onSubmitCheck = (e: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
    e.preventDefault()
    setErrorInternal('')

    if (!commander) {
      setErrorInternal('Error: Commander is required')
      return
    }

    onSubmit({ commander, commander2, decklist, excludeLands })
  }

  const onClear = () => {
    setCommander('')
    setCommander2('')
    setDecklist('')
    setExcludeLands(false)
  }

  const onClearCommander = useCallback(() => setCommander(''), [])
  const onClearCommander2 = useCallback(() => setCommander2(''), [])

  return (
    <form className={styles.form} onSubmit={onSubmitCheck}>
      <div className={styles.container}>
        {errorDisplay && <AlertBox>{errorDisplay}</AlertBox>}
        <div className={styles.commanders}>
          <div className={styles.commander}>
            <div className={styles.label}>Commander</div>
            <InputTypeahead
              aria-label='Search for a commander'
              disabled={loading}
              minCharacters={3}
              mode='commander'
              onClear={onClearCommander}
              onSelect={setCommander}
              placeholder='Search for a commander...'
              required
              size='lg'
              value={commander}
            />
          </div>
          <div className={styles.commander}>
            <div className={styles.label}>Commander #2</div>
            <InputTypeahead
              aria-label='Search for a partner commander'
              disabled={loading}
              minCharacters={3}
              mode='partner'
              onClear={onClearCommander2}
              onSelect={setCommander2}
              placeholder='(optional)'
              size='lg'
              value={commander2}
            />
          </div>
        </div>
        <div>
          <label className={styles.label} htmlFor='decklist'>
            Decklist
          </label>
          <InputTextarea
            disabled={loading}
            id='decklist'
            name='decklist'
            onChange={setDecklist}
            placeholder={`Accepts several formats, e.g. ...\r\n...\r\nSol Ring\r\n1 Sun Titan\r\n1x Vandalblast`}
            value={decklist}
          />
        </div>
        <div>
          <ButtonCheckbox
            disabled={loading}
            onClick={() => setExcludeLands(!excludeLands)}
            selected={excludeLands}
            variant='simple'
            wrap
          >
            Exclude Lands
          </ButtonCheckbox>
        </div>
        <div className={styles.actions}>
          <div>
            <ButtonDefault disabled={disabled} loading={loading} size='lg' type='submit'>
              Submit
            </ButtonDefault>
          </div>
          {clearable && (
            <ButtonText hover='warning' onClick={onClear} size='lg'>
              Clear
            </ButtonText>
          )}
        </div>
      </div>
    </form>
  )
}
